import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search, X } from 'lucide-react';
import { usePublicCategories } from '../../../../hooks/useCategories';
import clsx from 'clsx';
import * as Icons from 'lucide-react';

interface CategorySelectorProps {
    selectedIds: string[];
    onChange: (ids: string[]) => void;
}

export const CategorySelector: React.FC<CategorySelectorProps> = ({ selectedIds, onChange }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language.startsWith('ar') ? 'ar' : 'en';
    const [searchTerm, setSearchTerm] = useState('');

    const { data: categories = [], isLoading } = usePublicCategories();

    const toggleCategory = (id: string) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter(cid => cid !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    const getLabel = (cat: any) => {
        return cat.name?.[currentLang] || cat.label?.[currentLang] || cat.name?.en || cat.label?.en || cat.slug || cat.label || cat.id;
    };

    const filteredCategories = (categories as any[]).filter(c => {
        const label = getLabel(c);
        return label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.slug?.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const sortedCategories = [...filteredCategories].sort((a, b) => {
        const aSelected = selectedIds.includes(a.id);
        const bSelected = selectedIds.includes(b.id);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return getLabel(a).localeCompare(getLabel(b));
    });

    if (isLoading) return <div className="p-4 text-center text-slate-500 text-sm">Loading categories...</div>;

    return (
        <div className="space-y-4">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                    type="text"
                    placeholder="Search property types..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none"
                    dir="auto"
                />
            </div>

            <div className="border rounded-lg max-h-60 overflow-y-auto">
                {sortedCategories.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 text-sm">No categories found.</div>
                ) : (
                    <div className="divide-y relative">
                        {sortedCategories.map(cat => {
                            const isSelected = selectedIds.includes(cat.id);
                            const label = getLabel(cat);
                            const IconComponent = (Icons as any)[cat.icon || 'Home'];

                            return (
                                <div
                                    key={cat.id}
                                    onClick={() => toggleCategory(cat.id)}
                                    className={clsx(
                                        "flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors",
                                        isSelected && "bg-primary/5"
                                    )}
                                >
                                    <div className={clsx(
                                        "w-5 h-5 rounded border flex items-center justify-center transition-colors shrink-0",
                                        isSelected ? "bg-primary border-primary text-white" : "border-slate-300 bg-white"
                                    )}>
                                        {isSelected && <Check className="w-3 h-3" />}
                                    </div>

                                    {IconComponent && (
                                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                            <IconComponent className="w-4 h-4 text-slate-500" />
                                        </div>
                                    )}

                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-900 truncate">
                                            {label}
                                        </p>
                                        <p className="text-[10px] uppercase tracking-wider text-slate-400 truncate">
                                            {cat.slug}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {selectedIds.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                    {selectedIds.map(id => {
                        const cat = (categories as any[]).find(c => c.id === id);
                        if (!cat) return null;
                        return (
                            <div key={id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 border rounded-full text-xs">
                                <span className="max-w-[120px] truncate font-medium text-slate-700">{getLabel(cat)}</span>
                                <button
                                    onClick={() => toggleCategory(id)}
                                    className="hover:text-red-500 p-0.5"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
